import { BufferHelper } from 'hls.js/src/utils/buffer-helper';
import { findFragmentByPDT, findFragmentByPTS } from 'hls.js/src/controller/fragment-finders';

import { ISubtitleStreamController } from './IHlsJsExtended';
import { HlsPlayer } from './HlsPlayer';
import { HlsJsStreamControllerState } from './HlsJsStreamControllerState';

const FRAG_BUFFER_TOLERANCE_SECOND = 20;
const MAX_CONFIG_BUFFER_SECOND = 100;

export class SubtitleHelper {
	private hlsJs: any;

	constructor(
		private hlsPlayer: HlsPlayer
	) {
		this.hlsJs = hlsPlayer.player;
		this.hlsJs.on('hlsMediaAttached', () => this.onMediaAttached());
	}

	public setSubtitleTrack(showSubtitle: boolean, subtitle: TextTrack): void {
		this.hlsJs.subtitleDisplay = showSubtitle;
		const newTrack = this.hlsJs.subtitleTracks?.find(item => item.lang === subtitle?.language);
		this.hlsJs.subtitleTrack = newTrack ? newTrack.id : -1;
	}

	//workaround where subtitletrackcontroller state stuck in FRAG_LOADING
	//In that case, it wont fetch next vtt file. Setting it back to idle.
	public onSubtitleTrackLoaded(data): void {
		const controller = this.getSutitleStreamController();
		if (controller?.state === HlsJsStreamControllerState.FRAG_LOADING) {
			controller.state = HlsJsStreamControllerState.IDLE;
		}
		this.recalculateSubtitleFragCC(data.details);
	}

	/**
	 * This is almost a copy of hls.js SubtitleStreamController doTick method with some of our tweaks.
	 * esp - bufferEnd < end && (end - bufferEnd) <= FRAG_BUFFER_TOLERANCE_SECOND condition
	 * This is needed because in case of dual playback player tends to recover from multiple media-error
	 * In recovery process, it reinitialized the video element and sets back currentTime to 0
	 * and at the same time subtitle tracks way ahead in time. Hls.js should consider initPTs time
	 * while selecting fragment.
	 */
	public assignSubtitleStreamDoTick(): void {
		const controller = this.getSutitleStreamController();
		controller.doTick = () => {
			if (!controller.media) {
				controller.state = HlsJsStreamControllerState.IDLE;
				return;
			}

			switch (controller.state) {
				case HlsJsStreamControllerState.IDLE: {
					const { config, currentTrackId, fragmentTracker, media, tracks } = controller;
					if (!tracks || !tracks[currentTrackId] || !tracks[currentTrackId].details) {
						break;
					}

					const { maxBufferHole, maxFragLookUpTolerance } = config;
					const bufferedInfo = BufferHelper.bufferedInfo(controller._getBuffered(), media.currentTime, maxBufferHole);
					const { end: bufferEnd, len: bufferLen } = bufferedInfo;

					const trackDetails = tracks[currentTrackId].details;
					const fragments = trackDetails.fragments;
					const fragLen = fragments.length;
					const end = fragments[fragLen - 1].start + fragments[fragLen - 1].duration;

					if (bufferLen > MAX_CONFIG_BUFFER_SECOND) {
						return;
					}

					let foundFrag;
					const fragPrevious = controller.fragPrevious;
					if (bufferEnd < end && (end - bufferEnd) <= FRAG_BUFFER_TOLERANCE_SECOND) {
						if (fragPrevious && trackDetails.hasProgramDateTime) {
							foundFrag = findFragmentByPDT(fragments, fragPrevious.endProgramDateTime, maxFragLookUpTolerance);
						}
						if (!foundFrag) {
							foundFrag = findFragmentByPTS(fragPrevious, fragments, bufferEnd, maxFragLookUpTolerance);
						}
					} else {
						foundFrag = fragments[fragLen - 1];
					}
					if (foundFrag && foundFrag.encrypted) {
						controller.state = 'KEY_LOADING';
						controller.hls.trigger('hlsKeyLoading', { frag: foundFrag });
					} else if (foundFrag && fragmentTracker.getState(foundFrag) === 'NOT_LOADED') {
						// only load if fragment is not loaded
						controller.fragCurrent = foundFrag;
						controller.state = HlsJsStreamControllerState.FRAG_LOADING;
						controller.hls.trigger('hlsFragLoading', { frag: foundFrag });
					}
				}
			}
		}

	}

	//on live scenario, when playback is stopped and resumed
	//need to clean up subtitles related data.
	public onCleanupSubtitlesData(): void {
		const controller = this.getSutitleStreamController();
		controller?.tracks?.forEach(track => {
			if (track.details) {
				track.details.fragments = [];
			}
			this.resetTracksBuffer(controller, track.id);
		})

		const timeController = this.hlsPlayer.getTimelineController();
		if (timeController) {
			timeController.initPTS = [];
		}
	}

	private onMediaAttached(): void {
		this.resetTracksBuffer(this.getSutitleStreamController(), this.hlsJs.subtitleTrack);
	}

	private resetTracksBuffer(controller: ISubtitleStreamController, trackId: string): void {
		if (controller.tracksBuffered?.[trackId]?.length) {
			controller.tracksBuffered[trackId] = [];
		}
	}


	private getSutitleStreamController(): ISubtitleStreamController {
		return this.hlsJs.networkControllers.find(controller => 'tracksBuffered' in controller);
	}

	/**
	 * work around - when live is stopped and restarted from source
	 * mismatch happening between subtitle frag cc and playlist frag cc
	 * because subtitle playlist adding #EXT-X-DISCONTINUITY first and then EXT-X-DISCONTINUITY-SEQUENCE
	 * but playback playlist only adds #EXT-X-DISCONTINUITY and not EXT-X-DISCONTINUITY-SEQUENCE
	 */
	private recalculateSubtitleFragCC(details: any): void {
		const timeController = this.hlsPlayer.getTimelineController();
		const initPtsMaxIndex = timeController?.initPTS?.length - 1;
		const frags = details?.fragments;
		let ccChanged: boolean;

		if (initPtsMaxIndex >= 0 && frags?.length) {
			frags.forEach(frag => {
				if (frag.cc !== initPtsMaxIndex) {
					ccChanged = true;
					frag.cc = initPtsMaxIndex;
				}
			})

			if (ccChanged) {
				details.startCC = frags[0].cc;
				details.endCC = frags[frags.length - 1].cc;
			}
		}
	}
}
